import styled from '@emotion/styled'
import { IGenericObject } from '@types'
import React, { useEffect, useRef, useState } from 'react'
import { Feature, Geometry, GeoJsonProperties } from 'geojson'
import Map, { MapRef, ViewStateChangeEvent } from 'react-map-gl'
import mapboxgl, { GeoJSONSource } from 'mapbox-gl'
import * as methodUtils from './utils/methods'

import { Button, IOption, Select } from '@ss/meta-core-components'
import { Label } from '@styles'
import { config } from '../../config'
import useRest from '../../hooks/useRest'
import {
  ESBoundingBox,
  GeoSearchData,
  LayerValues,
  LegendData,
} from './utils/interfaces'
import * as dataUtils from './utils/data'

/**
 * Pagina Geo Analysis
 * @component
 */
export const GeoAnalysis: React.FC = () => {
  const mapRef = useRef<MapRef>(null)
  const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true })

  const { post: getGeoHeatmap } = useRest('/geo_heatmap')
  const { post: getGeoSearch } = useRest('/geo_search')

  // #region State
  const [boundingBox, setBoundingBox] = useState<ESBoundingBox>()
  const [enableSearch, setEnableSearch] = useState<boolean>(true)
  const [visibleStartups, setVisibleStartups] = useState<number>(0)
  const [visibleSearch, setVisibleSearch] = useState<boolean>(false)
  const [viewState, setViewState] = useState(dataUtils.initialViewport)
  const [filters, setFilters] = useState<IGenericObject<IOption | null>>({})
  const [legendLabels, setLegendLabels] = useState<LegendData>(
    dataUtils.initialLegendState
  )
  // #endregion

  // #region Map Events
  const onMapLoad = React.useCallback(() => {
    const map = mapRef.current?.getMap()
    map?.addSource('heatmap-source', dataUtils.heatmapSourceConfig)
    map?.addLayer(dataUtils.heatmapLayerConfig)
    map?.addLayer(dataUtils.circleLayerConfig)
  }, [])

  const onMapClick = (e: mapboxgl.MapLayerMouseEvent) => {
    const features = e.features
    if (features === undefined || features.length === 0) return

    const zoom = e.target.getZoom()
    const activeLayer = dataUtils.zoomBreakpoints.find((b) => b.zoom > zoom)
    const activeFeatures = features.filter(
      (f) => f.layer.id === activeLayer?.layer
    )

    const map = mapRef.current?.getMap()
    if (map && activeFeatures && activeFeatures.length > 0) {
      const popupContent = activeFeatures
        .map((f) => {
          const name =
            f.properties?.name ||
            f.properties?.prov_name ||
            f.properties?.reg_name ||
            ''
          const value =
            activeLayer?.layer === 'circle-layer'
              ? f.properties?.value
              : f.state?.value
          const formattedValue = value
            ? value.toLocaleString('it-IT')
            : 'Non presente'
          return '<p><b>' + name + ': </b>' + formattedValue + '</p>'
        })
        .join('')

      popup.setLngLat(e.lngLat).setHTML(popupContent).addTo(map)
    }
  }

  const onMapMove = React.useCallback((evt: ViewStateChangeEvent) => {
    setViewState(evt.viewState)
    setEnableSearch(true)

    if (evt.viewState.zoom >= 11) {
      setVisibleSearch(true)
      const map = mapRef.current?.getMap()
      if (!map) return

      const bounds = map.getBounds()
      const [nw, se] = [bounds.getNorthWest(), bounds.getSouthEast()]

      setBoundingBox({
        top_left: { lat: nw.lat, lon: nw.lng },
        bottom_right: { lat: se.lat, lon: se.lng },
      })
    } else {
      setVisibleSearch(false)
    }
  }, [])

  const onMapZoom = (e: ViewStateChangeEvent) => {
    const zoom = e.viewState.zoom
    const activeLayer = dataUtils.zoomBreakpoints.find((b) => b.zoom > zoom)
    if (!activeLayer) return

    setLegendLabels({
      activeLayer: {
        id: activeLayer.layer as LayerValues,
        label: activeLayer.label,
      },
      ranges: { ...legendLabels.ranges },
    })
  }
  // #endregion

  const onSearchWithViewport = () => {
    setEnableSearch(false)

    if (!boundingBox) return

    getGeoSearch(
      methodUtils.generateGeoSearch(
        boundingBox,
        filters.field?.value as string,
        filters.anno?.value as number
      )
    ).then((resp) => {
      const data: GeoSearchData[] = resp.data

      const map = mapRef.current?.getMap()
      const source = map?.getSource('heatmap-source') as GeoJSONSource

      const features: Feature<Geometry, GeoJsonProperties>[] = data.map((d) => {
        return {
          id: d.denominazione,
          type: 'Feature',
          geometry: { type: 'Point', coordinates: d.posizione },
          properties: { name: d.denominazione, value: d.value || 0 },
        }
      })

      const min_value = features.reduce(
        (current_min, f) => Math.min(f.properties?.value, current_min),
        1000000000
      )
      const max_value = features.reduce(
        (current_max, f) => Math.max(f.properties?.value, current_max),
        -1000000000
      )

      setVisibleStartups(features.length)

      methodUtils.setupCircleLayerPaintStyle(mapRef, min_value, max_value)

      source.setData({
        type: 'FeatureCollection',
        features: features,
      })
    })
  }

  const geoValuesToHeatmap = (
    resp: IGenericObject<any>,
    features: mapboxgl.MapboxGeoJSONFeature[],
    layer: mapboxgl.FillLayer,
    mapping: { [index: string]: string } | undefined,
    featureName: string
  ) => {
    const map = mapRef.current?.getMap()
    const data = resp.data as {
      name: string
      value: number
      scaled_value: number
    }[]

    features?.forEach((f) => {
      const nomeRegione = f.properties?.[featureName] as string
      const value = data.find((d) => {
        if (mapping) {
          return (
            d.name.toLowerCase() ===
            (mapping[nomeRegione] || nomeRegione).toLowerCase()
          )
        } else return d.name.toLowerCase() === nomeRegione.toLowerCase()
      })

      if (value === undefined) return

      map?.setFeatureState(
        {
          id: f.id,
          layer: layer,
          source: layer.source as string,
          sourceLayer: (layer as any).sourceLayer,
        },
        { geo_value: value.scaled_value, value: value.value }
      )
    })

    methodUtils.setupLayerFillColorInterpolation(mapRef, [layer], {
      [layer.id]: [resp.scaled_min_value, resp.scaled_max_value],
    })
  }

  const resetMapViewport = () => {
    mapRef.current?.easeTo({
      center: [
        dataUtils.initialViewport.longitude,
        dataUtils.initialViewport.latitude,
      ],
      zoom: dataUtils.initialViewport.zoom,
      duration: 1000,
    })
  }

  const correctFilters = (): boolean => {
    if (!filters.anno || filters.anno.value === '') return false
    if (!filters.field || filters.field.value === '') return false
    return true
  }

  const updateSingleFilter = (
    options: IOption[] | undefined,
    filterName: string
  ) => {
    setFilters({
      ...filters,
      [filterName]: options?.[0] ? options?.[0] : null,
    })
  }

  useEffect(() => {
    if (!correctFilters()) return

    const [layerRegioni, layerProvince, layerComuni] = [
      ...methodUtils.getMapLayers(mapRef),
    ]
    const layers = [layerRegioni, layerProvince, layerComuni]

    const [featuresRegioni, featuresProvince, featuresComuni] = [
      ...methodUtils.getLayerFeatures(mapRef, layers),
    ]

    const filterValues: [string, number, string] = [
      filters.field?.value as string,
      filters.anno?.value as number,
      filters.modalita?.value as string,
    ]

    const promises = [
      getGeoHeatmap(methodUtils.generateGeoHeatmap(...filterValues, 'regione')),
      getGeoHeatmap(
        methodUtils.generateGeoHeatmap(...filterValues, 'provincia')
      ),
      getGeoHeatmap(methodUtils.generateGeoHeatmap(...filterValues, 'comune')),
    ]

    Promise.all(promises).then((responses) => {
      geoValuesToHeatmap(
        responses[0],
        featuresRegioni || [],
        layerRegioni,
        dataUtils.mappingNomiRegione,
        'reg_name'
      )
      geoValuesToHeatmap(
        responses[1],
        featuresProvince || [],
        layerProvince,
        undefined,
        'prov_acr'
      )
      geoValuesToHeatmap(
        responses[2],
        featuresComuni || [],
        layerComuni,
        dataUtils.mappingNomiComuni,
        'name'
      )

      setLegendLabels({
        activeLayer: legendLabels.activeLayer,
        ranges: {
          'limits-it-regions': [responses[0].min_value, responses[0].max_value],
          'limits-it-provinces': [
            responses[1].min_value,
            responses[1].max_value,
          ],
          'limits-it-municipalities': [
            responses[2].min_value,
            responses[2].max_value,
          ],
          'circle-layer': [responses[2].min_value, responses[2].max_value],
        },
      })
    })
  }, [filters])

  return (
    <Container>
      <GeoForm>
        <InputContainer data-cy={'select-metrics'}>
          <Label>Seleziona Campo</Label>
          <Select
            dataCy={'select-metrics'}
            options={config.filters.geoAttributes}
            onChange={(options) => updateSingleFilter(options, 'field')}
          />
        </InputContainer>

        <InputContainer data-cy={'select-metrics'}>
          <Label>Seleziona Anno</Label>
          <Select
            dataCy={'select-metrics'}
            options={config.filters.years}
            onChange={(options) => updateSingleFilter(options, 'anno')}
          />
        </InputContainer>

        <InputContainer data-cy={'select-metrics'}>
          <Label>Modalità</Label>
          <Select
            dataCy={'select-metrics'}
            options={[
              { value: 'sum', label: 'Sum' },
              { value: 'avg', label: 'Average' },
              { value: 'min', label: 'Minimum' },
              { value: 'max', label: 'Maximum' },
            ]}
            onChange={(options) => updateSingleFilter(options, 'modalita')}
          />
        </InputContainer>
        <Button onClick={resetMapViewport}>Reset Mappa</Button>
      </GeoForm>

      <Map
        {...viewState}
        ref={mapRef}
        style={{ width: '100%', height: '100%' }}
        onMove={onMapMove}
        onZoom={onMapZoom}
        onLoad={onMapLoad}
        onClick={onMapClick}
        interactiveLayerIds={[
          'circle-layer',
          'limits-it-regions',
          'limits-it-provinces',
          'limits-it-municipalities',
        ]}
        mapStyle='mapbox://styles/metamorphosis-keybiz/cl9r85hl3003014nm8oj4ztl4?fresh=true'
        mapboxAccessToken='pk.eyJ1IjoibWV0YW1vcnBob3Npcy1rZXliaXoiLCJhIjoiY2tyM2NmMnNyMHk5ejJxcW02enA3cjF1ciJ9.BmbJ7NhbUzb1qbf6bKDB-Q'
      />

      {visibleSearch && (
        <BoundingBox>
          {!enableSearch && (
            <>
              <p>Startup visibili: {visibleStartups}</p>
              <p>
                Per effettuare una ricerca su una nuova area, muovere la mappa
              </p>
            </>
          )}

          {enableSearch && (
            <SearchButton onClick={onSearchWithViewport}>
              Cerca in questa area
            </SearchButton>
          )}
        </BoundingBox>
      )}

      <LegendContainer>
        <LegendTitle>
          <div>
            <b>Layer attivo</b>
          </div>
          <div>{legendLabels.activeLayer.label}</div>
        </LegendTitle>
        <LegendContent>
          <LegendGradient />
          <LegendLabels>
            <div>
              {legendLabels.ranges[
                legendLabels.activeLayer.id
              ][1].toLocaleString('it-IT') || ''}
            </div>
            <div>
              {legendLabels.ranges[
                legendLabels.activeLayer.id
              ][0].toLocaleString('it-IT') || ''}
            </div>
          </LegendLabels>
        </LegendContent>
      </LegendContainer>
    </Container>
  )
}

// #region STYLE
const Container = styled.div`
  display: flex;
  padding: 0;
  width: 100%;
`
const BoundingBox = styled.div`
  position: absolute;
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 40%;
  left: 30%;
  bottom: 50px;
  height: 60px;
  padding: 10px;
  background-color: #254661;
  border: 1px solid #ffffff;
  border-radius: 10px;
`
const SearchButton = styled.button`
  padding: 5px 10px;
`
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`
const GeoForm = styled.div`
  position: absolute;
  display: flex;
  gap: 20px;
  top: 50px;
  left: 50px;
  z-index: 100;
  background-color: #254661;
  border: 1px solid #ffffff;
  border-radius: 10px;
  padding: 20px;
  align-items: center;
  justify-content: space-around;
`

const LegendContainer = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  width: 120px;
  left: 50px;
  bottom: 50px;
  height: 280px;
`

const LegendContent = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`

const LegendTitle = styled.div`
  display: flex;
  flex-direction: column;
  height: 60px;
  text-align: center;
  padding: 10px;
  width: 100%;
`

const LegendGradient = styled.div`
  width: 20px;
  height: 100%;
  background-color: #666666;
  border: 1px solid #ffffff;
  border-radius: 10px;
  background: rgb(81, 176, 210);
  background: linear-gradient(
    0deg,
    rgba(81, 176, 210, 0.44) 0%,
    rgba(245, 0, 0, 0.44) 100%
  );
`

const LegendLabels = styled.div`
  width: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: 10px;
`
// #endregion
